import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastContainer';
import {
  getCompanyInfo,
  getInvites,
  getCompanyUsers,
  inviteUser,
  deleteInvite,
  getPublicPricingPlans,
  getCompanyConnectionStats,
} from '../services/api';
import { uniquePlanFeatures } from '../utils/planFeatures';
import './Company.css';

const tabs = ['overview', 'team', 'billing'];

const Company = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const [companyInfo, setCompanyInfo] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [plans, setPlans] = useState([]);
  const [connStats, setConnStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [teamQuery, setTeamQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [info, inviteList, memberList, pricing, stats] = await Promise.all([
        getCompanyInfo(),
        getInvites().catch(() => []),
        getCompanyUsers().catch(() => []),
        getPublicPricingPlans().catch(() => []),
        getCompanyConnectionStats().catch(() => null),
      ]);
      setCompanyInfo(info);
      setInvites(Array.isArray(inviteList) ? inviteList : []);
      setMembers(Array.isArray(memberList) ? memberList : []);
      setPlans(Array.isArray(pricing) ? pricing : []);
      setConnStats(stats);
    } catch (err) {
      showError(err.message || t('company.loadError', 'Failed to load company information'));
    } finally {
      setLoading(false);
    }
  };

  const company = companyInfo?.company;
  const license = companyInfo?.license;
  const userCount = companyInfo?.user_count || members.length || 0;
  const maxUsers = companyInfo?.max_users || license?.max_users || 0;
  const invoices = companyInfo?.invoices || [];
  const activeConnections =
    companyInfo?.active_connections ??
    connStats?.active_connections ??
    0;
  const pendingInvites =
    companyInfo?.pending_invites ??
    invites.filter((invite) => !invite.is_used).length;

  const currentPlanKey = (license?.plan_type || 'free').toLowerCase();

  const planMeta = useMemo(() => {
    const match = plans.find(
      (plan) => String(plan.plan_key || '').toLowerCase() === currentPlanKey
    );
    if (match) {
      return {
        key: currentPlanKey,
        label: match.display_name,
        price:
          match.price_monthly > 0
            ? `${match.price_monthly} ${match.currency}/mo`
            : t('company.plan.custom', 'Custom'),
        features: Array.isArray(match.features) ? match.features : [],
        maxConnections: match.max_connections,
        maxUsers: match.max_users,
      };
    }
    return {
      key: currentPlanKey,
      label: currentPlanKey.charAt(0).toUpperCase() + currentPlanKey.slice(1),
      price: '—',
      features: [],
      maxConnections: license?.max_connections || 0,
      maxUsers: maxUsers,
    };
  }, [plans, currentPlanKey, license, maxUsers, t]);

  const userPct = maxUsers > 0 ? Math.min(100, (userCount / maxUsers) * 100) : 0;
  const connPct =
    license?.max_connections > 0
      ? Math.min(100, (activeConnections / license.max_connections) * 100)
      : 0;

  const filteredMembers = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (member) =>
        member.username?.toLowerCase().includes(q) ||
        member.email?.toLowerCase().includes(q)
    );
  }, [members, teamQuery]);

  const pendingInviteList = invites.filter((invite) => !invite.is_used);
  const acceptedInviteList = invites.filter((invite) => invite.is_used);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const response = await inviteUser(inviteEmail.trim());
      const code = response?.invite_code;
      if (response?.email_sent === false && code) {
        showSuccess(
          t(
            'company.invite.createdNoEmail',
            'Invite created. Share this code: {{code}}',
            { code }
          )
        );
      } else {
        showSuccess(
          t('company.invite.sent', 'Invitation sent to {{email}}', {
            email: inviteEmail.trim(),
          })
        );
      }
      setInviteEmail('');
      await loadAll();
      setActiveTab('team');
    } catch (err) {
      showError(err.message || t('company.invite.failed', 'Failed to send invitation'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await deleteInvite(inviteId);
      showSuccess(t('company.invites.cancelled', 'Invitation cancelled'));
      await loadAll();
    } catch (err) {
      showError(err.message || t('company.invites.cancelFailed', 'Failed to cancel invitation'));
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      showSuccess(t('company.invites.copied', 'Invite code copied'));
      setTimeout(() => setCopiedCode(''), 1500);
    } catch {
      showError(t('company.invites.copyFailed', 'Could not copy code'));
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatMoney = (amount, currency = 'USD') => {
    const n = Number(amount || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${currency || ''}`.trim();
    }
  };

  if (loading) {
    return (
      <div className="co-page">
        <div className="co-empty">
          <div className="co-spinner" />
          <p>{t('company.loading', 'Loading company…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      <section className="co-hero">
        <div className="co-hero-copy">
          <p className="co-kicker">{t('company.kicker', 'Company')}</p>
          <h1>{company?.name || t('company.untitled', 'Your company')}</h1>
          <p className="co-lead">
            {company?.domain
              ? company.domain
              : t(
                  'company.lead',
                  'Manage seats, invites, plan limits, and billing from one place.'
                )}
          </p>
          <p className="co-lead-mobile">
            {company?.domain
              ? company.domain
              : t('company.leadMobile', 'Manage seats, invites, and billing.')}
          </p>
          <div className="co-hero-actions">
            <button type="button" className="co-btn primary" onClick={() => setActiveTab('team')}>
              {t('company.cta.invite', 'Invite teammate')}
            </button>
            <button type="button" className="co-btn ghost" onClick={() => setShowUpgradeModal(true)}>
              {t('company.cta.upgrade', 'View plans')}
            </button>
          </div>
        </div>
        <div className="co-hero-stats">
          <div className="co-stat">
            <strong>
              {userCount}
              {maxUsers > 0 ? <em>/{maxUsers}</em> : null}
            </strong>
            <span>{t('company.stats.seats', 'Seats')}</span>
          </div>
          <div className="co-stat">
            <strong>
              {activeConnections}
              {license?.max_connections ? <em>/{license.max_connections}</em> : null}
            </strong>
            <span>{t('company.stats.connections', 'Active links')}</span>
          </div>
          <div className="co-stat">
            <strong>{pendingInvites}</strong>
            <span>{t('company.stats.pending', 'Pending invites')}</span>
          </div>
        </div>
      </section>

      <nav className="co-tabs" aria-label="Company sections">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`co-tab ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && t('company.tabs.overview', 'Overview')}
            {tab === 'team' && t('company.tabs.team', 'Team')}
            {tab === 'billing' && t('company.tabs.billing', 'Billing')}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' && (
        <section className="co-grid">
          <article className="co-panel co-plan">
            <header className="co-panel-head">
              <div>
                <p className="co-kicker">{t('company.license.title', 'Current plan')}</p>
                <h2>{planMeta.label}</h2>
              </div>
              <span className="co-badge">{planMeta.price}</span>
            </header>

            <div className="co-meters">
              <div className="co-meter">
                <div className="co-meter-top">
                  <span>{t('company.users.title', 'Team seats')}</span>
                  <strong>
                    {userCount} / {maxUsers || '∞'}
                  </strong>
                </div>
                <div className="co-meter-track">
                  <div className="co-meter-fill" style={{ width: `${userPct}%` }} />
                </div>
              </div>
              <div className="co-meter">
                <div className="co-meter-top">
                  <span>{t('company.license.maxConnections', 'Connections')}</span>
                  <strong>
                    {activeConnections} / {license?.max_connections || '∞'}
                  </strong>
                </div>
                <div className="co-meter-track">
                  <div className="co-meter-fill" style={{ width: `${connPct}%` }} />
                </div>
              </div>
            </div>

            <div className="co-plan-meta">
              <div>
                <span>{t('company.license.expiresAt', 'Expires')}</span>
                <strong>
                  {license?.expires_at
                    ? formatDate(license.expires_at)
                    : t('company.license.noExpiry', 'No expiry')}
                </strong>
              </div>
              <div>
                <span>{t('company.license.status', 'Status')}</span>
                <strong className={license?.is_active ? 'is-ok' : 'is-bad'}>
                  {license?.is_active
                    ? t('company.license.active', 'Active')
                    : t('company.license.inactive', 'Inactive')}
                </strong>
              </div>
            </div>

            {planMeta.features?.length > 0 && (
              <ul className="co-feature-list">
                {planMeta.features.slice(0, 6).map((feature) => (
                  <li key={feature}>
                    <span>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            )}

            <button type="button" className="co-btn primary" onClick={() => setShowUpgradeModal(true)}>
              {t('company.license.upgrade', 'Upgrade plan')}
            </button>
          </article>

          <article className="co-panel">
            <header className="co-panel-head">
              <div>
                <p className="co-kicker">{t('company.team.snapshot', 'Team snapshot')}</p>
                <h2>{t('company.team.members', 'Members')}</h2>
              </div>
              <button type="button" className="co-btn ghost" onClick={() => setActiveTab('team')}>
                {t('company.team.manage', 'Manage')}
              </button>
            </header>
            <ul className="co-member-preview">
              {members.slice(0, 5).map((member) => (
                <li key={member.id}>
                  <span className="co-avatar">{(member.username || member.email || '?').charAt(0).toUpperCase()}</span>
                  <div>
                    <strong>{member.username}</strong>
                    <em>{member.email}</em>
                  </div>
                  {member.is_company_admin && (
                    <span className="co-chip">{t('company.team.admin', 'Admin')}</span>
                  )}
                </li>
              ))}
              {members.length === 0 && (
                <li className="co-muted">{t('company.team.empty', 'No members loaded yet.')}</li>
              )}
            </ul>
            {pendingInvites > 0 && (
              <p className="co-note">
                {t('company.team.pendingNote', '{{count}} pending invite(s) waiting to be accepted.', {
                  count: pendingInvites,
                })}
              </p>
            )}
          </article>
        </section>
      )}

      {activeTab === 'team' && (
        <section className="co-grid co-team-grid">
          <article className="co-panel">
            <header className="co-panel-head">
              <div>
                <p className="co-kicker">{t('company.invite.title', 'Invite')}</p>
                <h2>{t('company.invite.heading', 'Add a teammate')}</h2>
              </div>
            </header>
            <p className="co-help">
              {t(
                'company.invite.help',
                'We email an invite code. They can join during signup with that code.'
              )}
            </p>
            <form className="co-invite-form" onSubmit={handleInviteUser}>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('company.invite.placeholder', 'name@company.com')}
                required
                disabled={inviteLoading}
              />
              <button type="submit" className="co-btn primary" disabled={inviteLoading || !inviteEmail}>
                {inviteLoading
                  ? t('company.invite.sending', 'Sending…')
                  : t('company.invite.button', 'Send invite')}
              </button>
            </form>
            {maxUsers > 0 && (
              <p className="co-note">
                {(() => {
                  const left = Math.max(0, maxUsers - userCount - pendingInvites);
                  if (left === 0) {
                    return t('company.invite.noSeat', 'No seat');
                  }
                  if (left === 1) {
                    return t('company.invite.oneSeat', '1 seat available');
                  }
                  return t('company.invite.seatsLeft', '{{left}} seats available', { left });
                })()}
              </p>
            )}
          </article>

          <article className="co-panel co-span-2">
            <header className="co-panel-head">
              <div>
                <p className="co-kicker">{t('company.team.directory', 'Directory')}</p>
                <h2>
                  {t('company.team.members', 'Members')} ({members.length})
                </h2>
              </div>
              <input
                className="co-search"
                type="search"
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder={t('company.team.search', 'Search members…')}
              />
            </header>
            <div className="co-table-wrap">
              <table className="co-table">
                <thead>
                  <tr>
                    <th>{t('company.team.user', 'User')}</th>
                    <th>{t('company.team.role', 'Role')}</th>
                    <th>{t('company.team.status', 'Status')}</th>
                    <th>{t('company.team.joined', 'Joined')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.id}>
                      <td data-label={t('company.team.user', 'User')}>
                        <div className="co-user-cell">
                          <span className="co-avatar">
                            {(member.username || member.email || '?').charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <strong>{member.username}</strong>
                            <em>{member.email}</em>
                          </div>
                        </div>
                      </td>
                      <td data-label={t('company.team.role', 'Role')}>
                        {member.is_company_admin
                          ? t('company.team.admin', 'Admin')
                          : t('company.team.member', 'Member')}
                      </td>
                      <td data-label={t('company.team.status', 'Status')}>
                        <span className={`co-chip ${member.is_active ? 'ok' : 'bad'}`}>
                          {member.is_active
                            ? t('company.team.active', 'Active')
                            : t('company.team.inactive', 'Inactive')}
                        </span>
                      </td>
                      <td data-label={t('company.team.joined', 'Joined')}>{formatDate(member.created_at)}</td>
                    </tr>
                  ))}
                  {filteredMembers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="co-muted">
                        {t('company.team.none', 'No members match your search.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="co-panel co-span-full">
            <header className="co-panel-head">
              <div>
                <p className="co-kicker">{t('company.invites.title', 'Invitations')}</p>
                <h2>
                  {t('company.invites.pending', 'Pending')} ({pendingInviteList.length})
                </h2>
              </div>
            </header>
            {pendingInviteList.length === 0 ? (
              <p className="co-muted">{t('company.invites.empty', 'No pending invitations.')}</p>
            ) : (
              <ul className="co-invite-list">
                {pendingInviteList.map((invite) => (
                  <li key={invite.id}>
                    <div>
                      <strong>{invite.email}</strong>
                      <em>
                        {t('company.invites.sentOn', 'Sent {{date}}', {
                          date: formatDate(invite.created_at),
                        })}
                      </em>
                      {invite.invite_code && (
                        <div className="co-code-row">
                          <code>{invite.invite_code}</code>
                          <button type="button" className="co-btn ghost tiny" onClick={() => copyCode(invite.invite_code)}>
                            {copiedCode === invite.invite_code
                              ? t('company.invites.copiedShort', 'Copied')
                              : t('company.invites.copy', 'Copy code')}
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="co-btn danger ghost"
                      onClick={() => handleCancelInvite(invite.id)}
                    >
                      {t('company.invites.cancel', 'Cancel')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {acceptedInviteList.length > 0 && (
              <p className="co-note">
                {t('company.invites.acceptedNote', '{{count}} invite(s) already accepted.', {
                  count: acceptedInviteList.length,
                })}
              </p>
            )}
          </article>
        </section>
      )}

      {activeTab === 'billing' && (
        <section className="co-panel">
          <header className="co-panel-head">
            <div>
              <p className="co-kicker">{t('company.invoices.title', 'Billing')}</p>
              <h2>{t('company.invoices.history', 'Invoice history')}</h2>
            </div>
            <a className="co-btn ghost" href="mailto:sales@arcvpn.com">
              {t('company.invoices.contact', 'Contact sales')}
            </a>
          </header>
          {invoices.length === 0 ? (
            <div className="co-empty soft">
              <h3>{t('company.invoices.empty', 'No invoices yet')}</h3>
              <p>
                {t(
                  'company.invoices.emptyDesc',
                  'When billing starts, invoices will appear here.'
                )}
              </p>
            </div>
          ) : (
            <div className="co-table-wrap">
              <table className="co-table">
                <thead>
                  <tr>
                    <th>{t('company.invoices.date', 'Date')}</th>
                    <th>{t('company.invoices.plan', 'Plan')}</th>
                    <th>{t('company.invoices.amount', 'Amount')}</th>
                    <th>{t('company.invoices.status', 'Status')}</th>
                    <th>{t('company.invoices.dueDate', 'Due')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td data-label={t('company.invoices.date', 'Date')}>{formatDate(invoice.billing_date)}</td>
                      <td data-label={t('company.invoices.plan', 'Plan')}>
                        <span className="co-chip">{invoice.plan_type}</span>
                      </td>
                      <td className="co-amount" data-label={t('company.invoices.amount', 'Amount')}>
                        {formatMoney(invoice.amount, invoice.currency)}
                      </td>
                      <td data-label={t('company.invoices.status', 'Status')}>
                        <span className={`co-chip status-${invoice.status}`}>{invoice.status}</span>
                      </td>
                      <td data-label={t('company.invoices.dueDate', 'Due')}>{formatDate(invoice.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {showUpgradeModal && (
        <div className="co-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="co-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="co-modal-head">
              <div>
                <p className="co-kicker">{t('company.upgrade.title', 'Plans')}</p>
                <h2>{t('company.upgrade.heading', 'Choose a better fit')}</h2>
              </div>
              <button type="button" className="co-icon-btn" onClick={() => setShowUpgradeModal(false)}>
                ×
              </button>
            </header>
            <div className="co-plans">
              {(plans.length
                ? plans
                : [
                    { plan_key: 'free', display_name: 'Free', price_monthly: 0, currency: 'USD', features: [] },
                    { plan_key: 'basic', display_name: 'Basic', price_monthly: 29, currency: 'USD', features: [] },
                    { plan_key: 'pro', display_name: 'Pro', price_monthly: 79, currency: 'USD', features: [] },
                    { plan_key: 'enterprise', display_name: 'Enterprise', price_monthly: 0, currency: 'USD', features: [] },
                  ]
              ).map((plan) => {
                const key = String(plan.plan_key || '').toLowerCase();
                const isCurrent = key === currentPlanKey;
                return (
                  <article key={key} className={`co-plan-card ${isCurrent ? 'is-current' : ''}`}>
                    {isCurrent && (
                      <span className="co-plan-badge">{t('company.upgrade.current', 'Current plan')}</span>
                    )}
                    <h3>{plan.display_name}</h3>
                    <p className="co-plan-price">
                      {plan.price_monthly > 0
                        ? `${plan.price_monthly} ${plan.currency}/mo`
                        : t('company.plan.contact', 'Contact us')}
                    </p>
                    <ul>
                      <li>
                        {plan.max_users > 0
                          ? `${plan.max_users} ${t('company.plan.users', 'users')}`
                          : t('company.plan.unlimitedUsers', 'Unlimited users')}
                      </li>
                      <li>
                        {plan.max_connections > 0
                          ? `${plan.max_connections} ${t('company.plan.connections', 'connections')}`
                          : t('company.plan.unlimitedConnections', 'Unlimited connections')}
                      </li>
                      {uniquePlanFeatures(plan).map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <button type="button" className="co-btn ghost" disabled>
                        {t('company.upgrade.current', 'Current plan')}
                      </button>
                    ) : key === 'free' ? (
                      <button type="button" className="co-btn ghost" disabled>
                        {t('upgrade.plans.free', 'Free')}
                      </button>
                    ) : (
                      <Link
                        className="co-btn primary"
                        to={`/dashboard/checkout?plan=${encodeURIComponent(key)}`}
                        onClick={() => setShowUpgradeModal(false)}
                      >
                        {t('upgrade.buy', 'Buy & upgrade')}
                      </Link>
                    )}
                  </article>
                );
              })}
            </div>
            <p className="co-note center">
              {t(
                'company.upgrade.demoNote',
                'Demo checkout upgrades the plan and creates an invoice.'
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Company;
